import { attachmentApi, folderApi, handleError } from '@ardoq/api';
import { APIAttachment, ArdoqId } from '@ardoq/api-types';
import { toIds, toIdSet } from '@ardoq/core';
import { showImagePreviewModal } from '@ardoq/image-preview';
import {
  getImagePreviewErrorMessages,
  getImagePreviewLabels,
  getImagePreviewModalChrome,
} from '../../../locales/getImagePreviewLabels';
import {
  action$,
  collectRoutines,
  connect,
  dispatchAction,
  extractPayload,
  ofType,
  spreadMap,
} from '@ardoq/rxbeach';
import {
  AbsoluteScrollPosition,
  CellCoordinate,
  SortDescriptor,
} from '@ardoq/table';
import {
  combineLatest,
  delay,
  distinctUntilChanged,
  filter,
  from,
  map,
  mergeMap,
  of,
  shareReplay,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs';
import { OrgTabs } from '../../../admin/manageOrganization/types';
import {
  areFileSizesValid,
  toUploadFilesRejections,
} from '../../../documentArchive/attachmentUploadUtils';
import { DocumentArchiveContext } from '@ardoq/api-types';
import {
  cancelDelete,
  clearHighlightedRows,
  closeMoveToModal,
  confirmDelete,
  createWorkspaceFolder,
  deleteAttachments,
  deleteFolders,
  downloadAttachment,
  FilterExpression,
  highlightRows,
  ConfirmMoveToNewFolderPayload,
  confirmMoveToNewWorkspaceFolder,
  MoveAttachmentsPayload,
  MoveFolderPayload,
  moveWorkspaceAttachments,
  moveWorkspaceFolder,
  openMoveToModal,
  renameWorkspaceAttachment,
  renameWorkspaceFolder,
  requestDeletion,
  setEditedCell,
  setFilterExpressions,
  setFolderClosed,
  setFolderExpanded,
  setMoveToModalDestination,
  setMoveToModalNewFolderName,
  setMoveToModalSearchTerm,
  setScrollPosition,
  setSearchTerm,
  setSelectedIds,
  setSortDescriptor,
  showDragOverlay,
  UploadFilesPayload,
  uploadWorkspaceFiles,
  attachmentRenameRejected,
} from '../../../documentArchive/actions';
import {
  documentArchiveState$,
  globalDocumentArchiveAttachments$,
} from '../../../documentArchive/DocumentArchivePage';
import { downloadFile } from '../../../documentArchive/downloadFile';
import { filterRouteActive, location$ } from '../../../router/declarative';
import { navigateToManageOrganization } from '../../../router/navigationActions';
import { registerWorkspaces } from '../../../streams/context/ContextActions';
import {
  fetchWorkspaceDocumentArchiveAttachments,
  fetchWorkspaceDocumentArchiveFolders,
} from '../../../streams/documentArchive/actions';
import { documentArchiveAttachments$ } from '../../../streams/documentArchive/documentArchiveAttachments$';
import { documentArchiveFolders$ } from '../../../streams/documentArchive/documentArchiveFolders$';
import { i18nLocale$ } from '../../../streams/i18nLocale$';
import { locale$ } from '../../../streams/locale$';
import { trackingData } from '../../../tracking/trackingData';
import { scrollAndHighlightRow, transformToGlobal } from './actions';
import {
  toWorkspaceDocumentArchivePageViewProps,
  WorkspaceDocumentArchiveCommands,
} from './toWorkspaceDocumentArchivePageViewProps';
import { WorkspaceDocumentArchivePageView } from './WorkspaceDocumentArchivePageView';
import { dragState$ } from '../../streams/dragState$';

const workspaceId$ = location$.pipe(
  filterRouteActive('/workspace-manager/:workspaceId/workspace-documents'),
  map(location => location.params.workspaceId),
  distinctUntilChanged(),
  tap(workspaceId => {
    dispatchAction(fetchWorkspaceDocumentArchiveAttachments(workspaceId));
    dispatchAction(fetchWorkspaceDocumentArchiveFolders(workspaceId));
  }),
  // shareReplay is needed because this stream has a side effect (fetch actions) in its tap.
  // Without it, every new subscription (e.g., via withLatestFrom in attachments$)
  // would re-trigger fetches, causing unwanted re-fetches during file uploads.
  shareReplay({ bufferSize: 1, refCount: true })
);

const workspaceAttachments$ = documentArchiveAttachments$.pipe(
  map(s => s.models),
  map(attachments =>
    attachments.filter(a => a.context === DocumentArchiveContext.WS)
  ),
  withLatestFrom(workspaceId$),
  map(([attachments, workspaceId]) =>
    attachments.filter(attachment => attachment.rootWorkspace === workspaceId)
  )
);

const folders$ = documentArchiveFolders$.pipe(
  map(s => s.models),
  map(folders => folders.filter(f => f.context === DocumentArchiveContext.WS)),
  withLatestFrom(workspaceId$),
  map(([folders, workspaceId]) =>
    folders.filter(folder => folder.resource.id === workspaceId)
  )
);

const ensureWebsocketSubscription$ = workspaceId$.pipe(
  tap(workspaceId => {
    dispatchAction(registerWorkspaces({ workspaceIds: [workspaceId] }));
  })
);

const queueClearHighlight = action$.pipe(
  ofType(highlightRows),
  extractPayload(),
  delay(1000),
  tap(rowIds => {
    dispatchAction(clearHighlightedRows(rowIds));
  })
);

const handleMoveFolder = action$.pipe(
  ofType(moveWorkspaceFolder),
  extractPayload(),
  switchMap(({ typeId, foldersWithNewParent }) =>
    folderApi.moveMultipleToFolder({
      type: 'workspace',
      typeId,
      foldersWithNewParent,
    })
  ),
  handleError()
);

const handleMoveAttachments = action$.pipe(
  ofType(moveWorkspaceAttachments),
  extractPayload(),
  switchMap(attachmentApi.moveMultipleToFolder),
  handleError()
);

const handleRenameFolder = action$.pipe(
  ofType(renameWorkspaceFolder),
  extractPayload(),
  switchMap(payload =>
    folderApi.rename({
      type: 'workspace',
      typeId: payload.typeId,
      renameRequest: {
        _id: payload.id,
        _version: payload.version,
        name: payload.newName,
      },
    })
  ),
  handleError()
);

const handleRenameAttachment = action$.pipe(
  ofType(renameWorkspaceAttachment),
  extractPayload(),
  switchMap(payload =>
    attachmentApi.rename({
      type: 'workspace',
      typeId: payload.typeId,
      renameRequest: {
        _id: payload.id,
        _version: payload.version,
        name: payload.newName,
      },
    })
  ),
  handleError()
);

const handleConfirmDelete = action$.pipe(
  ofType(confirmDelete),
  extractPayload(),
  tap(payload => {
    dispatchAction(deleteFolders(payload.folderIds));
    dispatchAction(deleteAttachments(payload.attachmentIds));
  })
);

const handleDeleteFolder = action$.pipe(
  ofType(deleteFolders),
  extractPayload(),
  switchMap(folderApi.deleteMultiple),
  handleError()
);

const handleDeleteAttachments = action$.pipe(
  ofType(deleteAttachments),
  extractPayload(),
  switchMap(attachmentApi.deleteMultiple),
  handleError()
);

const handleDownloadAttachment = action$.pipe(
  ofType(downloadAttachment),
  extractPayload(),
  map(({ uri }) => downloadFile(uri))
);

// The folder isn't added to the documentArchiveFolders$ immediately after creation,
// so we need to wait for it to appear there before proceeding in order to successfully enter edit mode.
const waitForFolderToExist$ = (folderId: string) =>
  documentArchiveFolders$.pipe(
    map(s => s.models),
    filter(folders => folders.some(f => f._id === folderId)),
    take(1),
    map(() => folderId)
  );

const waitForAttachmentToExist$ = (uploadedAttachments: APIAttachment[]) => {
  const uploadedIds = toIdSet(uploadedAttachments);

  return documentArchiveAttachments$.pipe(
    map(s => s.models),
    map(attachments => attachments.filter(a => uploadedIds.has(a._id))),
    filter(filtered => filtered.length === uploadedAttachments.length),
    take(1)
  );
};

const handleCreateFolder = action$.pipe(
  ofType(createWorkspaceFolder),
  extractPayload(),
  switchMap(payload =>
    folderApi.create({
      name: payload.name,
      type: 'workspace',
      typeId: payload.typeId,
    })
  ),
  handleError(),
  switchMap(folder => waitForFolderToExist$(folder._id)),
  tap(folderId =>
    dispatchAction(setEditedCell({ rowId: folderId, columnId: '_group' }))
  )
);

const legalFileSizes = (payload: UploadFilesPayload): boolean =>
  areFileSizesValid(payload.files);

const notDuplicateNames = (payload: UploadFilesPayload): boolean =>
  !payload.files.some(file =>
    payload.existingAttachmentNames.includes(file.name)
  );

const handleUploadFiles = action$.pipe(
  ofType(uploadWorkspaceFiles),
  extractPayload(),
  filter(legalFileSizes),
  filter(notDuplicateNames),
  switchMap(payload =>
    attachmentApi.uploadFiles({
      files: payload.files,
      type: 'workspace',
      typeId: payload.typeId,
    })
  ),
  handleError(),
  switchMap(waitForAttachmentToExist$),
  tap(attachments => {
    dispatchAction(highlightRows(toIds(attachments)));
    dispatchAction(
      setScrollPosition({
        rowId: attachments[attachments.length - 1]._id,
        left: 0,
      })
    );
  })
);

const handleUploadWorkspaceFilesRejected = action$.pipe(
  ofType(uploadWorkspaceFiles),
  extractPayload(),
  mergeMap(toUploadFilesRejections),
  tap(dispatchAction)
);

const handleConfirmMoveToNewWorkspaceFolder = action$.pipe(
  ofType(confirmMoveToNewWorkspaceFolder),
  extractPayload(),
  switchMap(payload =>
    from(
      folderApi.create({
        name: payload.newFolderName,
        type: 'workspace',
        typeId: payload.typeId,
      })
    ).pipe(
      handleError(),
      switchMap(newFolder =>
        waitForFolderToExist$(newFolder._id).pipe(
          map(() => ({ folderId: newFolder._id, payload }))
        )
      )
    )
  ),
  tap(({ folderId, payload }) => {
    if (payload.foldersToMove.length > 0) {
      dispatchAction(
        moveWorkspaceFolder({
          typeId: payload.typeId,
          foldersWithNewParent: payload.foldersToMove.map(f => ({
            ...f,
            parent: folderId,
          })),
        })
      );
    }
    if (payload.attachmentsToMove.length > 0) {
      dispatchAction(
        moveWorkspaceAttachments({
          attachmentIds: payload.attachmentsToMove.map(a => a._id),
          folder: folderId,
        })
      );
    }
    dispatchAction(closeMoveToModal());
  })
);

export const workspaceDocumentArchiveCommands: WorkspaceDocumentArchiveCommands =
  {
    transformToGlobal: (
      workspaceId: string,
      attachmentIds: string[],
      folderIds: string[]
    ) => {
      dispatchAction(
        transformToGlobal({
          workspaceId,
          attachmentAndFolderIds: {
            attachmentIds,
            folderIds,
          },
        })
      );
    },
    toOrganizationArchive: () =>
      dispatchAction(
        navigateToManageOrganization({
          manageOrgTab: OrgTabs.DOCUMENT_ARCHIVE,
        })
      ),
    globalDocumentArchiveCommands: {
      moveToModal: {
        openMoveToModal: (folderIds: string[], attachmentIds: string[]) =>
          dispatchAction(openMoveToModal({ folderIds, attachmentIds })),
        closeMoveToModal: () => dispatchAction(closeMoveToModal()),
        setMoveToModalDestination: (folderId: string | 'root') =>
          dispatchAction(setMoveToModalDestination(folderId)),
        setMoveToModalSearchTerm: (searchTerm: string) =>
          dispatchAction(setMoveToModalSearchTerm(searchTerm)),
        setMoveToModalNewFolderName: (name: string | null) =>
          dispatchAction(setMoveToModalNewFolderName(name)),
        confirmMoveToNewFolder: (payload: ConfirmMoveToNewFolderPayload) =>
          dispatchAction(confirmMoveToNewWorkspaceFolder(payload)),
      },
      dropZone: {
        showDragOverlay: (show: boolean) =>
          dispatchAction(showDragOverlay(show)),
        uploadFiles: (
          files: File[],
          typeId: ArdoqId,
          existingAttachmentNames: string[],
          dropped: boolean
        ) =>
          dispatchAction(
            uploadWorkspaceFiles({
              files,
              typeId,
              existingAttachmentNames,

              [trackingData]: {
                event: 'Workspace document archive0 Uploaded files',
                payload: {
                  numberOfFiles: files.length,
                  dropped,
                },
              },
            })
          ),
      },
      deleteConfirmationModal: {
        confirmDelete: (folderIds: string[], attachmentIds: string[]) =>
          dispatchAction(
            confirmDelete({
              folderIds,
              attachmentIds,

              [trackingData]: {
                event: 'Workspace document archive0 Confirm delete',
                payload: {
                  numberOfFolders: folderIds.length,
                  numberOfAttachments: attachmentIds.length,
                },
              },
            })
          ),
        cancelDelete: () =>
          dispatchAction(
            cancelDelete({
              [trackingData]: {
                event: 'Workspace document archive0 Cancel delete',
              },
            })
          ),
      },
      documentArchiveTable: {
        setSearchTerm: (term: string) => dispatchAction(setSearchTerm(term)),
        setSortDescriptor: (descriptor: SortDescriptor | null) =>
          dispatchAction(setSortDescriptor(descriptor)),
        setFilterExpressions: (expressions: FilterExpression[]) =>
          dispatchAction(setFilterExpressions(expressions)),
        setFolderExpanded: (folderId: string) =>
          dispatchAction(setFolderExpanded(folderId)),
        setFolderClosed: (folderId: string) =>
          dispatchAction(setFolderClosed(folderId)),
        setSelectedIds: (ids: string[]) => dispatchAction(setSelectedIds(ids)),
        createFolder: (name: string, typeId: ArdoqId) =>
          dispatchAction(
            createWorkspaceFolder({
              name,
              typeId,

              [trackingData]: {
                event: 'Workspace document archive0 Created folder',
              },
            })
          ),
        renameAttachment: (
          id: string,
          newName: string,
          typeId: ArdoqId,
          version: number
        ) =>
          dispatchAction(
            renameWorkspaceAttachment({
              id,
              version,
              newName,
              typeId,

              [trackingData]: {
                event: 'Workspace document archive0 Renamed item',
                payload: {
                  itemType: 'attachment',
                },
              },
            })
          ),
        rejectAttachmentRename: () =>
          dispatchAction(attachmentRenameRejected()),
        renameFolder: (
          id: string,
          newName: string,
          typeId: ArdoqId,
          version: number
        ) =>
          dispatchAction(
            renameWorkspaceFolder({
              id,
              version,
              newName,
              typeId,

              [trackingData]: {
                event: 'Workspace document archive0 Renamed item',
                payload: {
                  itemType: 'folder',
                },
              },
            })
          ),

        folderMenuItem: {
          setEditedCell: (coordinate: CellCoordinate | null) =>
            dispatchAction(setEditedCell(coordinate)),
          requestDeletion: (attachmentIds: string[], folderIds: string[]) =>
            dispatchAction(
              requestDeletion({
                folderIds,
                attachmentIds,
                [trackingData]: {
                  event: 'Workspace document archive0 Request deletion',
                },
              })
            ),
          openMoveToModal: (folderIds: string[], attachmentIds: string[]) =>
            dispatchAction(openMoveToModal({ folderIds, attachmentIds })),
        },
        attachmentMenuItem: {
          showImagePreviewModal: (title: string, url: string) => {
            // i18nLocale$.state OK here: snapshot-at-open-time; @ardoq/modal renders modal() body once and does not re-render on locale change
            const i18nLocale = i18nLocale$.state;
            const { downloadLinkLabel, closeButtonText } =
              getImagePreviewModalChrome(i18nLocale);
            return showImagePreviewModal({
              title,
              url,
              link: { href: url, label: downloadLinkLabel },
              closeButtonText,
              labels: getImagePreviewLabels(i18nLocale),
              errorMessages: getImagePreviewErrorMessages(i18nLocale),
            });
          },
          setEditedCell: (coordinate: CellCoordinate | null) =>
            dispatchAction(setEditedCell(coordinate)),
          requestDeletion: (attachmentIds: string[], folderIds: string[]) =>
            dispatchAction(
              requestDeletion({
                folderIds,
                attachmentIds,
                [trackingData]: {
                  event: 'Workspace document archive0 Request deletion',
                },
              })
            ),
          downloadAttachment: (uri: string) =>
            dispatchAction(
              downloadAttachment({
                uri,
                [trackingData]: {
                  event: 'Workspace document archive0 Downloaded attachment',
                },
              })
            ),
          openMoveToModal: (folderIds: string[], attachmentIds: string[]) =>
            dispatchAction(openMoveToModal({ folderIds, attachmentIds })),
        },
        dragAndDroppable: {
          moveFolder: (payload: MoveFolderPayload) =>
            dispatchAction(
              moveWorkspaceFolder({
                ...payload,
                [trackingData]: {
                  event: 'Workspace document archive0 Moved item',
                  payload: {
                    itemType: 'folder',
                    numberOfItems: 1,
                  },
                },
              })
            ),
          moveAttachments: (payload: MoveAttachmentsPayload) =>
            dispatchAction(
              moveWorkspaceAttachments({
                ...payload,
                [trackingData]: {
                  event: 'Workspace document archive0 Moved item',
                  payload: {
                    itemType: 'attachment',
                    numberOfItems: payload.attachmentIds.length,
                  },
                },
              })
            ),
        },
        scrollable: {
          setScrollPosition: (scrollPosition: AbsoluteScrollPosition) =>
            dispatchAction(setScrollPosition(scrollPosition)),
        },
      },
    },
  };

const handleTransformToGlobal = action$.pipe(
  ofType(transformToGlobal),
  extractPayload(),
  switchMap(attachmentApi.transformToGlobal),
  handleError()
);

const handleScrollAndHighlightRow = action$.pipe(
  ofType(scrollAndHighlightRow),
  extractPayload(),
  tap(rowId => {
    dispatchAction(highlightRows([rowId]));
    dispatchAction(
      setScrollPosition({
        rowId,
        left: 0,
      })
    );
  })
);

export const workspaceDocumentArchiveRoutines = collectRoutines(
  handleRenameFolder,
  handleRenameAttachment,
  handleConfirmDelete,
  handleDeleteFolder,
  handleDeleteAttachments,
  handleDownloadAttachment,
  handleMoveFolder,
  handleMoveAttachments,
  handleCreateFolder,
  handleUploadFiles,
  handleUploadWorkspaceFilesRejected,
  queueClearHighlight,
  ensureWebsocketSubscription$,
  handleTransformToGlobal,
  handleScrollAndHighlightRow,
  handleConfirmMoveToNewWorkspaceFolder
);

export const WorkspaceDocumentArchivePage = connect(
  WorkspaceDocumentArchivePageView,
  combineLatest([
    documentArchiveState$,
    folders$,
    workspaceAttachments$,
    globalDocumentArchiveAttachments$,
    workspaceId$,
    locale$,
    of(workspaceDocumentArchiveCommands),
    dragState$,
    i18nLocale$,
  ]).pipe(spreadMap(toWorkspaceDocumentArchivePageViewProps)),
  workspaceDocumentArchiveRoutines
);
